// Hiring in the EquaOS frame, on its own address (hiring.<school>.equaos.in).
//
// The owner, 29 Oct 2026: every app must look exactly the same, "from the same
// UI frame". So Hiring does what People does (DPS-Employee-Management
// client/standalone.jsx): the page starts @equaos/frame, the ERP app with one
// module in it, and the frame mounts Hiring's screens inside it. The sidebar,
// the school's card, the person menu (Profile, Your data, Sign-in and security,
// Switch school, Appearance, Sign out), the assistant, the background and the
// session are all the frame's (ERP-Console docs/EQUAOS-FRAME.md).
//
// The frame is vendored in public/frame (scripts/vendor-frame.js) and loaded at
// run time through the page's import map, never bundled. It imports Hiring's
// remote (public/mfe/hiring-remote.js), whose mount() lands in mountInFrame
// below, in this bundle, already on the page.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation, useNavigationType } from 'react-router-dom';
import App from './App';
import { setFrameContext, bootShell } from './shell';

export const FRAME_URL = '/frame/vendor/equaos-frame.js';
export const REMOTE_URL = '/mfe/hiring-remote.js';

/* The frame lights the sidebar from the address it was last told of: its own
   navigate, or a popstate. A move made by Hiring's router (a link, a button)
   is a pushState the frame does not see, so it is told with a popstate, as the
   frame tells a module of its own moves. A move that was itself a popstate is
   left alone, so the two never echo. */
function TellFrame() {
  const { pathname, search, key } = useLocation();
  const type = useNavigationType();
  React.useEffect(() => {
    if (type === 'POP') return;
    try { window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state })); } catch { /* old browser */ }
  }, [pathname, search, key, type]);
  return null;
}

/* The frame's mount(el, ctx): Hiring's screens, content only, into el. */
export async function mountInFrame(el, ctx) {
  setFrameContext(ctx);
  await bootShell();
  const root = ReactDOM.createRoot(el);
  root.render(
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <TellFrame />
      <App />
    </BrowserRouter>
  );
  return {
    unmount() {
      root.unmount();
      setFrameContext(null);
    },
  };
}

/* The frame's own sheets, as People's page lists them: the design system's
   fonts and tokens, then the frame's. The old DPS sheet is switched off here:
   the frame's look is the whole page's. */
const FRAME_SHEETS = [
  'ds/fonts/poppins.css', 'ds/tokens/colors.css', 'ds/tokens/typography.css', 'ds/tokens/spacing.css',
  'ds/tokens/radii.css', 'ds/tokens/shadows.css', 'ds/tokens/motion.css', 'ds/tokens/base.css',
  'vendor/equaos-frame.css',
];

function frameStyles() {
  const legacy = document.getElementById('dps-legacy-styles');
  if (legacy) legacy.disabled = true;
  document.documentElement.setAttribute('data-theme', document.documentElement.getAttribute('data-theme') || 'light');
  for (const sheet of FRAME_SHEETS) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `/frame/${sheet}`;
    document.head.appendChild(link);
  }
}

/* Start the frame on this page. It signs the person in through the school's
   one sign-in, then imports the remote and mounts Hiring. */
export function startFrame() {
  window.__hiringMount = mountInFrame;
  frameStyles();
  return import(/* webpackIgnore: true */ FRAME_URL).then(({ startFrame: start }) => {
    start({ module: 'hiring', remote: REMOTE_URL, title: 'Hiring' });
  });
}
